/* Okendo remains the media/curation owner. Resolve product links by exact review ID. */
(() => {
  if (customElements.get('apolis-okendo-media')) return;
  const reviews = new Map();
  function subscriberId() {
    try { return JSON.parse(document.getElementById('oke-reviews-settings')?.textContent || '{}').subscriberId; }
    catch { return null; }
  }
  function destination(review) {
    if (review?.status !== 'approved' || !review.productUrl) return null;
    const url = new URL(review.productUrl, location.origin);
    if (url.origin !== location.origin || !/^\/products\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname)) return null;
    url.search = ''; url.hash = '';
    if (/^\d+$/.test(String(review.variantId || ''))) url.searchParams.set('variant', review.variantId);
    return url.href;
  }
  const attempts = new Map();
  const terminal = new Set();
  const MAX_ATTEMPTS = 3;
  const RETRY_DELAYS = [1000, 3000];
  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
  async function requestReview(store, id, key) {
    while ((attempts.get(key) || 0) < MAX_ATTEMPTS) {
      const attempt = (attempts.get(key) || 0) + 1;
      attempts.set(key, attempt);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      let retryable = true;
      try {
        const response = await fetch(`https://api.okendo.io/v1/stores/${encodeURIComponent(store)}/reviews/${encodeURIComponent(id)}`, {
          credentials: 'omit', signal: controller.signal
        });
        if (!response.ok) {
          retryable = response.status === 408 || response.status === 429 || response.status >= 500;
          if (!retryable) terminal.add(key);
          throw new Error('Review unavailable');
        }
        const data = await response.json();
        return data.review;
      } catch (error) {
        // JSON syntax is permanent; aborted/failed body reads are transport failures.
        if (error.name === 'SyntaxError') retryable = false;
        if (!retryable) terminal.add(key);
        if (!retryable || attempt >= MAX_ATTEMPTS) throw error;
      } finally {
        clearTimeout(timeout);
      }
      await wait(RETRY_DELAYS[attempt - 1]);
    }
    return null;
  }
  function loadReview(store, id) {
    const key = `${store}/${id}`;
    if (reviews.has(key)) return reviews.get(key);
    if (terminal.has(key) || (attempts.get(key) || 0) >= MAX_ATTEMPTS) return Promise.resolve(null);
    const pending = requestReview(store, id, key).catch(error => {
      reviews.delete(key);
      throw error;
    });
    reviews.set(key, pending);
    return pending;
  }
  customElements.define('apolis-okendo-media', class extends HTMLElement {
    connectedCallback() {
      this.seen = new WeakSet();
      this.visibility = new IntersectionObserver(entries => {
        for (const entry of entries) if (entry.isIntersecting) { this.visibility.unobserve(entry.target); this.link(entry.target); }
      }, { rootMargin: '500px' });
      this.changes = new MutationObserver(() => this.scan());
      this.changes.observe(this, { childList: true, subtree: true });
      this.scan();
    }
    disconnectedCallback() { this.visibility?.disconnect(); this.changes?.disconnect(); }
    scan() {
      for (const overlay of this.querySelectorAll('[data-oke-reviews-review-id]')) {
        if (this.seen.has(overlay)) continue;
        const label = overlay.querySelector('.okeReviews-mediaCarousel-overlay-link');
        if (!label) continue;
        this.seen.add(overlay);
        if (label.tagName === 'A') continue;
        label.textContent = 'View review';
        this.visibility.observe(overlay);
      }
    }
    async link(overlay) {
      const id = overlay.getAttribute('data-oke-reviews-review-id'), store = subscriberId();
      if (!/^[a-f0-9-]{36}$/i.test(id || '') || !/^[a-f0-9-]{36}$/i.test(store || '')) return;
      try {
        const review = await loadReview(store, id), url = destination(review);
        if (!this.isConnected || !overlay.isConnected || review?.reviewId !== id || !url) return;
        const label = overlay.querySelector('.okeReviews-mediaCarousel-overlay-link');
        if (!label || label.tagName === 'A') return;
        const link = document.createElement('a');
        link.className = label.className; link.href = url; link.textContent = 'Shop this bag';
        link.setAttribute('aria-label', `Shop ${review.productName || 'this bag'}`);
        // Flickity's staticClick opens a review modal on pointer-up. Isolate only
        // this explicit link, leaving photo dragging and video/gallery interaction intact.
        for (const event of ['pointerdown', 'mousedown', 'touchstart', 'pointerup', 'mouseup', 'touchend', 'click']) {
          link.addEventListener(event, e => e.stopPropagation());
        }
        label.replaceWith(link);
      } catch { /* Native review gallery remains available; do not invent a destination. */ }
    }
  });
})();
